// eslint-disable import-helpers/order-imports
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef
} from 'react'
import ReactDatePicker from 'react-datepicker'

import { Icon } from '@clientbase/clientbase-library'
import { ptBR } from 'date-fns/locale'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import 'react-datepicker/dist/react-datepicker.css'

import * as S from './DatePicker.styles'

export interface DatePickerHandles {
  handleUpdateCalendar: () => void
}

export type DateProps = Date | [Date | null, Date | null] | null
type HandleDataChangeEventParams = {
  date: DateProps
}

export type FilterKey =
  | 'due_date_between'
  | 'date_paid_between'
  | 'transfer_date_between'
  | 'date_between'
  | 'created_at_between'
  | 'signed_at_between'
  | 'competence_date_between'
  | 'statistics_due_date_between'
  | 'statistics_date_paid_between'
  | 'date_sent_between'
  | 'occurred_between'

export type DatePickerProps = {
  onChangeDate?: ({ value }: { value: DateProps }) => void
  initialDate: Date | null
  lastDate: Date | null
  filterKey: FilterKey
  handleDeleteFilters?: () => ParsedUrlQuery
  triggered?: boolean
  setTriggered: Dispatch<SetStateAction<boolean>>
}

const DatePicker = forwardRef<DatePickerHandles, DatePickerProps>(
  (
    {
      onChangeDate,
      initialDate = new Date(),
      lastDate = new Date(),
      filterKey,
      handleDeleteFilters,
      triggered,
      setTriggered
    },
    ref
  ) => {
    const { push, query } = useRouter()
    const calendarRef = useRef<ReactDatePicker | null>(null)

    const [startDate, setStartDate] = useState<Date | null>(initialDate)
    const [endDate, setEndDate] = useState<Date | null>(lastDate)
    const [previewDate, setPreviewDate] = useState<DateProps>([
      initialDate,
      lastDate
    ])

    const isArrayPreveiwDate = Array.isArray(previewDate)

    const handleChange = ({ date }: HandleDataChangeEventParams) => {
      setEndDate(null)
      setStartDate(null)

      if (!Array.isArray(date)) {
        setPreviewDate(date)
      } else {
        const [start, end] = date
        setPreviewDate([start, end])
      }
      if (onChangeDate) {
        onChangeDate({ value: date })
      }
    }

    const setSearchParamsDate = (date: DateProps) => {
      // SC-11829: sem data inicial o valor saía "undefined|undefined" e a API
      // respondia "invalid date". Sem data não há filtro para aplicar.
      if (!date) return

      if (Array.isArray(date)) {
        const [start, end] = date as Date[]
        if (!start) return

        const startAt = start?.toISOString().split('T')[0]
        const endAt = end ? end?.toISOString().split('T')[0] : startAt
        const value = `${startAt}|${endAt}`

        const filters = handleDeleteFilters ? handleDeleteFilters() : query
        push({ query: { ...filters, [filterKey]: value } })
        return
      }

      if (typeof date === 'object') {
        const day = (date as Date).toISOString().split('T')[0]
        const value = `${day}|${day}`
        const filters = handleDeleteFilters ? handleDeleteFilters() : query
        push({ query: { ...filters, [filterKey]: value } })
        return
      }
    }

    const handleUpdateCalendar = () => {
      if (onChangeDate) {
        onChangeDate({ value: previewDate })
      }

      if (!Array.isArray(previewDate)) {
        setStartDate(previewDate)
        setEndDate(null)
        setSearchParamsDate(previewDate)
      }

      if (Array.isArray(previewDate)) {
        setStartDate(previewDate[0])
        setEndDate(previewDate[1])
        setSearchParamsDate(previewDate)
      }
    }

    useImperativeHandle(ref, () => ({
      handleUpdateCalendar
    }))

    useEffect(() => {
      if (triggered) {
        handleUpdateCalendar()
        setTriggered(false)
      }
    }, [triggered])

    return (
      <ReactDatePicker
        ref={calendarRef}
        locale={ptBR}
        onChange={(date) => handleChange({ date })}
        startDate={
          isArrayPreveiwDate ? previewDate[0] : previewDate || startDate
        }
        endDate={(isArrayPreveiwDate && previewDate[1]) || endDate}
        selectsRange
        inline
        calendarContainer={({ children }) => <S.Content>{children}</S.Content>}
        timeIntervals={15}
        dateFormat="P"
        shouldCloseOnSelect={false}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled
        }) => (
          <S.HeaderWrapper>
            <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
              <Icon height="14px" width="14px" icon="arrowBackIosNew"></Icon>
            </button>

            {new Intl.DateTimeFormat('pt-BR', {
              month: 'long',
              year: 'numeric'
            }).format(date)}

            <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
              <Icon height="14px" width="14px" icon="arrowForwardIos"></Icon>
            </button>
          </S.HeaderWrapper>
        )}
      ></ReactDatePicker>
    )
  }
)
DatePicker.displayName = 'DatePicker'
export default DatePicker
